import { Api } from "lavva.exalushome";
import { DataFrame, Method, Status } from "lavva.exalushome/build/js/DataFrame";
import { ExtensionInfo } from "lavva.exalushome/build/js/Services/Controller/ControllerExtensionsService";
import { ExalusConnectionService } from "lavva.exalushome/build/js/Services/ExalusConnectionService";
import { ResponseResult } from "lavva.exalushome/build/js/Services/FieldChangeResult";
import { IExalusConnectionService } from "lavva.exalushome/build/js/Services/IExalusConnectionService";
import { ILoggerService } from "lavva.exalushome/build/js/Services/Logging/ILoggerService";
import { LoggerService } from "lavva.exalushome/build/js/Services/Logging/LoggerService";
import { UpdatesProvider, SoftwareRuntimeInfo } from "lavva.exalushome/build/js/Services/Updates/UpdatesProvider";
import { IUpdatesProvider, ProviderType, Update, UpdateInfo, UpdateTypes } from "lavva.exalushome/build/js/Services/Updates/IUpdatesProvider";
import { UpdateAction, UpdateErrorCode, UpdateProgress, UpdateProgressInfo, UpdateStatus } from "lavva.exalushome/build/js/Services/Updates/IUpdatesService";
import { IControllerModulesUpdatesProviderService } from "lavva.exalushome/build/js/Services/Updates/UpdateProviderServices/IControllerModulesUpdatesProviderService";
import { FirmwareVersion } from "../../Devices/DeviceServices/FirmwareService/FirmwareService";
import { ExtaLifeService } from "../../ExtaLife";
import { ICustomUpdateActions } from "lavva.exalushome/build/js/Services/Updates/CustomUpdateActions/ICustomUpdateActions";
import { Progress } from "lavva.exalushome/build/js/Services/Updates/IUpdatesService"

export class ExtaLifeControllerRadioUpdatesProvider implements IControllerModulesUpdatesProviderService {

    public static readonly ProviderName = "ExtaLifeControllerUpdateProvider";

    private _apiVersion: string;
    private _extenstionVersion: string;
    private _protocolExtGuid: string;
    private _runtimeInfo: SoftwareRuntimeInfo;
    private _logger: ILoggerService;
    private _connection: IExalusConnectionService;

    constructor(extInfo: ExtensionInfo, runtimeInfo: SoftwareRuntimeInfo) {
        this._apiVersion = extInfo.APIVersion;
        this._extenstionVersion = extInfo.ExtensionVersion;
        this._protocolExtGuid = extInfo.ExtensionGuid;
        this._runtimeInfo = runtimeInfo;

        this._logger = Api.Get<ILoggerService>(LoggerService.ServiceName);
        this._connection = Api.Get<IExalusConnectionService>(ExalusConnectionService.ServiceName);
    }
    get AvailableExtensionVersion(): string {
        return this._extenstionVersion;
    }

    get AvailableApiVersion(): string {
        return this._apiVersion;
    }

    get ExtensionGuid(): string {
        return this._protocolExtGuid;
    }

    get ProtocolGuid(): string {
        return ExtaLifeService.ExtaLifeProtocolGuid;
    }

    get ProviderType(): ProviderType {
        return ProviderType.Controller;
    }

    GetUpdateProviderName(): string {
        return `${ExtaLifeControllerRadioUpdatesProvider.ProviderName}_${this._apiVersion}_${this._extenstionVersion}`;
    }

    async GetAvailableRadioUpdateAsync(): Promise<UpdateInfo | ResponseResult<UpdateErrorCode>> {
        this._logger.Debug(ExtaLifeControllerRadioUpdatesProvider.ProviderName, `Checking for 868MHz radio firmware upgrade...`);

        const hadrwareInf = await Api.Get<IUpdatesProvider>(UpdatesProvider.ServiceName).GetHardwareInfoAsync();
        if (hadrwareInf.GetAvailableHardware.first(hInfo => hInfo.DevicePath == "/proc")?.DisplayName.contains("AM3352"))
            return new ResponseResult(UpdateErrorCode.UnsupportedDevice, `Cannot get available updates! This ExtaLife module does not support updates.`);

        if (parseFloat(this._extenstionVersion) < 1.2105051130) {
            return new ResponseResult(UpdateErrorCode.UnsupportedExtensionVersion, `Cannot get available updates! ExtaLife 868MHz extension is too old for upgrade. Version: ${this._extenstionVersion}`);
        }

        const result = await this._connection?.SendAndWaitForResponseAsync<RadioVersionResult>(new GetRadioVersionRequest(), 8000, false);
        let radioVersion = 1.0;

        if (result != null && result.Data != null && result.Data?.Item1)
            radioVersion = parseFloat(`${result.Data.Item2.Major}.${result.Data.Item2.Minor}`);

        this._logger.Debug(ExtaLifeControllerRadioUpdatesProvider.ProviderName, `ExtaLife 868MHz radio firmware version: ${radioVersion}`);

        var url = `https://updates.tr7.pl/firmware/${this._runtimeInfo.UpdateChannel}/${radioVersion}/${ExtaLifeService.ExtaLifeRadioModuleDeviceGuid}`;

        try {
            this._logger.Debug(`Looking for update at: ${url}/${radioVersion}/update.json`);
            let response = await fetch(`${url}/${radioVersion}/update.json`);
            let updateData;
            let updateInfo = new UpdateInfo();

            switch (response.status) {
                case 200:
                    if (response.body != null)
                        updateData = await response.json() as Update;
                    break;
                default:
                    this._logger.Debug(`Looking for update at: ${url}/update.json`);
                    response = await fetch(`${url}/update.json`);
                    switch (response.status) {
                        case 200:
                            if (response.body != null)
                                updateData = await response.json() as Update;
                            break;
                        case 404:
                            return new ResponseResult(UpdateErrorCode.DeviceIsUpToDate, `Updates not found, radio ExtaLife 868MHz is already up-to-date.`);
                        default:
                            return new ResponseResult(UpdateErrorCode.CannotGetUpdates, `Response status code does not indicate success. Status: ${response.status}`);
                    }
            }

            if (updateData != null) {
                updateInfo.CurrentResourceVersion = radioVersion.toString();
                updateInfo.UpdatedResourceName = "Radio ExtaLife 868 MHz";
                updateInfo.Update = updateData;

                return updateInfo;
            }
            else
                throw (new Error(`Cannot get Update Data from response. Status: ${response.status}`));

        }
        catch (err) {
            return new ResponseResult(UpdateErrorCode.CannotGetUpdates, `Cannot get available updates! ${err}`);
        }
    }

    async GetCurrentRadioVersionAsync(): Promise<string> {
        const result = await this._connection?.SendAndWaitForResponseAsync<RadioVersionResult>(new GetRadioVersionRequest(), 8000, false);

        if (result != null && result.Data != null && result.Data?.Item1)
            return `${result.Data.Item2.Major}.${result.Data.Item2.Minor}`;

        return "n/a";
    }

    async InstallRadioUpdateAsync<T extends ICustomUpdateActions>(update: UpdateInfo, updateProgress: UpdateProgress, updateAction?: UpdateAction<T> | undefined): Promise<ResponseResult<UpdateErrorCode> | Status.OK> {
        if (updateAction != null)
            return new ResponseResult(UpdateErrorCode.UpdateActionsNotSupported, `Update service for given resource: ${update.UpdatedResourceName} does not support custom update actions.`);

        const hadrwareInf = await Api.Get<IUpdatesProvider>(UpdatesProvider.ServiceName).GetHardwareInfoAsync();
        if (hadrwareInf.GetAvailableHardware.first(hInfo => hInfo.DevicePath == "/proc")?.DisplayName.contains("AM3352"))
            return new ResponseResult(UpdateErrorCode.UnsupportedDevice, `Cannot get available updates! This ExtaLife module does not support updates.`);

        const progressInfo = new UpdateProgressInfo();
        progressInfo.UpdatedResourceName = new Map<string, string>([["pl-PL", update.UpdatedResourceName], ["en-US", update.UpdatedResourceName]]);

        try {
            await this._connection?.SendAndHandleResponseAsync<Progress>(new UpgradeRadioFirmwareRequest(update.Update.DownloadUri), 30000, (resp) => {

                switch (resp.Status) {
                    case Status.MultiDataResponseStart:
                        progressInfo.Status = UpdateStatus.PreparingForUpgrade;
                        break;
                    case Status.MultiDataResponse:
                        if (resp.Data == null) {
                            this._logger.Warning(ExtaLifeControllerRadioUpdatesProvider.ProviderName, `Recived update response without data! URI: ${update.Update.DownloadUri}`);
                            return;
                        }
                        progressInfo.Percentage = resp.Data;
                        progressInfo.Status = UpdateStatus.Upgrading;
                        break;
                    case Status.MultiDataResponseStop:
                        if (resp.Data) {
                            progressInfo.Percentage = 100;
                            progressInfo.Status = UpdateStatus.Upgraded;
                        }
                        else
                            progressInfo.Status = UpdateStatus.InstallationError;
                        break;     
                }

                this._logger.Debug(ExtaLifeControllerRadioUpdatesProvider.ProviderName, `Recived update progress response, percentage: ${progressInfo.Percentage}, status: ${progressInfo.Status}, resource name: ${update.UpdatedResourceName}`);
                updateProgress(progressInfo);
            }, false);
        }
        catch (error) {
            progressInfo.Percentage = 0;
            progressInfo.Status = UpdateStatus.Error;

            updateProgress(progressInfo);
            return new ResponseResult(UpdateErrorCode.CannotProcessUpdate, `Cannot process update, error: ${error}`)
        }
        return Status.OK;
    }
}

class GetRadioVersionRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/extalife/radio/firmware/version";
        this.Method = Method.Get;
    }
}

class RadioVersionResult {
    Item1: boolean = false;
    Item2: FirmwareVersion = new FirmwareVersion;
}

class UpgradeRadioFirmwareRequest extends DataFrame<string>{
    constructor(uri: string) {
        super();
        this.Resource = "/extafree2/radio/upgrade";
        this.Method = Method.Put;
        this.Data = uri;
    }
}